import { useCallback, useState } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { HeaderContextMenu } from './HeaderContextMenu';
import type { SortDir } from '../SortableHeader';

/**
 * The minimal sort surface the header menu needs — just the current state and an
 * explicit setter.  `UseSortApi<T>` satisfies this structurally, and a grid with
 * BESPOKE sort state (e.g. Data Center's ModuleListView, which uses its own
 * sortCol/sortDesc) can pass a tiny adapter.  This keeps the hook usable by every
 * grid regardless of how it tracks its sort.
 */
export interface HeaderMenuSort {
  sort: { key: string | null; dir: SortDir };
  setSortExplicit: (key: string | null, dir: SortDir) => void;
}

/**
 * Optional column-management surface for the header menu (Hide · Show All ·
 * Column Chooser). Supplied by a grid that owns a column model — typically via
 * `useColumnManager().columnMenu`. Omit it and the menu shows only the Sort
 * section (a grid with no column model, e.g. a fixed-column table).
 */
export interface HeaderMenuColumns {
  hideColumn: (field: string) => void;
  showAllColumns: () => void;
  hasHidden: boolean;
  /** False ⇒ Hide Column is disabled (it's the last visible column). */
  canHide: (field: string) => boolean;
  openChooser: (x: number, y: number) => void;
}

/**
 * Shared header right-click context menu wiring for ANY grid — the ONE place the
 * global grid features (Sort · … Hide / Show-all / Chooser as they land) are
 * implemented, so both the shared <AppDataGrid> AND the hand-rolled `.dv-tbl` /
 * `.bl-tbl` / reporting tables get the IDENTICAL menu (client 2026-08-03: "I want
 * all the grids, list AND detail view — global requirement").
 *
 * Usage in a custom-table grid that already has a `useSort` sorter:
 *
 *   const { openHeaderMenu, headerMenu } = useHeaderMenu(sorter);
 *   …
 *   <th onContextMenu={(e) => openHeaderMenu(e, { field: 'jobId', label: 'Job ID' })}>…</th>
 *   …
 *   {headerMenu}   // render ONCE anywhere in the grid's tree (it portals to body)
 *
 * The menu itself (<HeaderContextMenu>) owns positioning, viewport-clamping and
 * the transient-popup close rules (outside-click / Escape / scroll).
 */
export type HeaderMenuTarget = { field: string; label: string; sortable?: boolean };

export function useHeaderMenu(sorter: HeaderMenuSort, columns?: HeaderMenuColumns) {
  const [menu, setMenu] = useState<
    { field: string; label: string; sortable: boolean; x: number; y: number } | null
  >(null);

  const openHeaderMenu = useCallback((e: ReactMouseEvent, col: HeaderMenuTarget) => {
    e.preventDefault();
    setMenu({ field: col.field, label: col.label, sortable: col.sortable !== false, x: e.clientX, y: e.clientY });
  }, []);

  // Recomputed each render, so the active-direction tick reflects the live sort.
  const headerMenu = menu
    ? createPortal(
        <HeaderContextMenu
          column={{ field: menu.field, label: menu.label, sortable: menu.sortable }}
          sort={sorter.sort}
          position={{ x: menu.x, y: menu.y }}
          onSort={(dir) => sorter.setSortExplicit(dir === null ? null : menu.field, dir)}
          // Column-management items render only when the grid provides a model.
          {...(columns
            ? {
                onHideColumn: () => columns.hideColumn(menu.field),
                canHide: columns.canHide(menu.field),
                onShowAllColumns: columns.showAllColumns,
                hasHiddenColumns: columns.hasHidden,
                onColumnChooser: () => columns.openChooser(menu.x, menu.y),
              }
            : {})}
          onClose={() => setMenu(null)}
        />,
        document.body,
      )
    : null;

  return { openHeaderMenu, headerMenu };
}
